import { Component, type ErrorInfo, type ReactNode } from 'react'

/*
 * The last thing between a bug and a blank page.
 *
 * Every other surface in this workspace can afford to crash to a white screen:
 * the reader reloads, or signs in again, and nothing about the failure is the
 * point. This page's entire premise is being readable when something is broken,
 * and there are two live ways it can fail after first paint that no test upstream
 * of here will catch.
 *
 *   **A lazy chunk that will not load.** `/incidents/{reference}` is a dynamic
 *   import, served from the static host. A reader on a bad connection — the
 *   reader this page exists for — gets a rejected `import()`, and React's
 *   default for an unhandled render error is to unmount the whole tree. The
 *   status page would go blank on a slow connection while claiming to be the
 *   thing you check on a slow connection.
 *
 *   **A payload that gets past the parser.** `public-status.ts` is total by
 *   construction, and "by construction" is a claim about code that can be
 *   wrong. A crash here still leaves the shell and a way back.
 *
 * A class component because that is the only thing React gives for this, and
 * about half a kilobyte of the 7.1 kB left in the budget. Worth it: the
 * alternative is that the page's worst failure mode is silence.
 */

interface State {
  readonly failed: boolean
}

export class ErrorBoundary extends Component<{ readonly children: ReactNode }, State> {
  override state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* To the console and nowhere else. There is no error-reporting endpoint on
       this origin and there must not be one: it is anonymous, uncredentialed,
       and adding a POST to it would be the first thing on this page that talks
       back. */
    console.error('status page render failed', error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children

    return (
      <section role="alert" className="border border-critical bg-critical-tint p-4">
        <h2 className="text-sm font-medium text-critical">This page could not be displayed</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Something went wrong rendering the status page. This is a fault in the page itself and
          says nothing about the state of the platform.
        </p>
        <p className="mt-3 text-sm">
          <a href="/" className="rounded-xs text-text-emphasis underline underline-offset-2">
            Reload platform status
          </a>
        </p>
      </section>
    )
  }
}
